
import { Brain, BarChart4, TrendingUp, Zap, Shield, Clock, Eye, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import ScrollAnimation from './ScrollAnimation';

const FeatureSection = () => {
  const { t } = useLanguage();
  
  return (
    <section id="features" className="pt-12 md:pt-16 pb-20 md:pb-28 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
          <ScrollAnimation>
            <span className="inline-block px-3 py-1 bg-primary/8 text-primary text-sm font-medium rounded-full border border-primary/15 mb-5">
              {t('features.title')}
            </span>
          </ScrollAnimation>
          <ScrollAnimation>
            <h2 className="heading-lg" style={{ textWrap: 'balance' }}>
              {t('features.subtitle')}
            </h2>
          </ScrollAnimation>
          <ScrollAnimation>
            <p className="body-md text-muted-foreground mt-4" style={{ textWrap: 'pretty' }}>
              {t('features.description')}
            </p>
          </ScrollAnimation>
        </div>
        
        {/* Primary Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-16 md:mb-20">
          <PrimaryFeatureCard 
            icon={<Brain size={24} />}
            title={t('features.ocr.title')}
            description={t('features.ocr.description')}
            features={[t('features.ocr.feature1'), t('features.ocr.feature2'), t('features.ocr.feature3')]}
            accentColor="primary"
            delay={0}
          />
          <PrimaryFeatureCard 
            icon={<BarChart4 size={24} />}
            title={t('features.dcf.title')}
            description={t('features.dcf.description')}
            features={[t('features.dcf.feature1'), t('features.dcf.feature2'), t('features.dcf.feature3')]}
            accentColor="success"
            delay={80}
          />
          <PrimaryFeatureCard 
            icon={<TrendingUp size={24} />}
            title={t('features.analytics.title')}
            description={t('features.analytics.description')}
            features={[t('features.analytics.feature1'), t('features.analytics.feature2'), t('features.analytics.feature3')]}
            accentColor="warning"
            delay={160}
          />
        </div>

        {/* Stats strip */}
        <ScrollAnimation>
          <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-card to-muted/30 p-8 md:p-10 mb-16 md:mb-20 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
              <StatCard number={t('stats.accuracy.number')} label={t('stats.accuracy.label')} subtext={t('stats.accuracy.subtext')} icon={<Brain size={20} />} />
              <StatCard number={t('stats.documents.number')} label={t('stats.documents.label')} subtext={t('stats.documents.subtext')} icon={<BarChart4 size={20} />} />
              <StatCard number={t('stats.companies.number')} label={t('stats.companies.label')} subtext={t('stats.companies.subtext')} icon={<TrendingUp size={20} />} />
              <StatCard number={t('stats.time.number')} label={t('stats.time.label')} subtext={t('stats.time.subtext')} icon={<Zap size={20} />} />
            </div>
          </div>
        </ScrollAnimation>

        {/* Secondary features */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          <SecondaryFeatureCard icon={<Zap size={20} />} title={t('secondary.speed.title')} description={t('secondary.speed.description')} colorClass="text-warning" bgClass="bg-warning/10" />
          <SecondaryFeatureCard icon={<Shield size={20} />} title={t('secondary.security.title')} description={t('secondary.security.description')} colorClass="text-primary" bgClass="bg-primary/10" />
          <SecondaryFeatureCard icon={<Clock size={20} />} title={t('secondary.processing.title')} description={t('secondary.processing.description')} colorClass="text-success" bgClass="bg-success/10" />
          <SecondaryFeatureCard icon={<Eye size={20} />} title={t('secondary.accuracy.title')} description={t('secondary.accuracy.description')} colorClass="text-primary" bgClass="bg-primary/10" />
        </div>
      </div>
    </section>
  );
};

interface PrimaryFeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  accentColor: 'primary' | 'success' | 'warning';
  delay: number;
}

const accentMap = {
  primary: {
    iconBg: 'bg-primary/10 group-hover:bg-primary',
    iconText: 'text-primary group-hover:text-primary-foreground',
    line: 'bg-primary/60',
    check: 'text-primary',
  },
  success: {
    iconBg: 'bg-success/10 group-hover:bg-success',
    iconText: 'text-success group-hover:text-success-foreground',
    line: 'bg-success/60',
    check: 'text-success',
  },
  warning: {
    iconBg: 'bg-warning/10 group-hover:bg-warning',
    iconText: 'text-warning group-hover:text-warning-foreground',
    line: 'bg-warning/60',
    check: 'text-warning',
  },
};

const PrimaryFeatureCard = ({ icon, title, description, features, accentColor, delay }: PrimaryFeatureCardProps) => {
  const accent = accentMap[accentColor];
  return (
    <ScrollAnimation>
      <div 
        className="relative h-full rounded-2xl border border-border/60 bg-card p-6 md:p-8 shadow-sm hover:shadow-lg hover:-translate-y-1.5 active:scale-[0.98] transition-all duration-300 ease-out group cursor-default overflow-hidden"
        style={{ transitionDelay: `${delay}ms` }}
      >
        {/* Accent top line */}
        <div className={`absolute top-0 left-0 right-0 h-[3px] ${accent.line} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
        
        <div className={`w-12 h-12 rounded-xl ${accent.iconBg} flex items-center justify-center mb-5 ${accent.iconText} transition-colors duration-300`}>
          {icon}
        </div>
        <h3 className="text-lg font-semibold mb-2.5 tracking-tight text-foreground">{title}</h3>
        <p className="text-muted-foreground mb-5 leading-relaxed text-sm">{description}</p>
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2.5 text-sm text-muted-foreground">
              <div className={`w-5 h-5 rounded-full ${accent.iconBg.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                <ArrowRight size={11} className={accent.check} />
              </div>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </ScrollAnimation>
  );
};

const SecondaryFeatureCard = ({ icon, title, description, colorClass, bgClass }: { icon: React.ReactNode; title: string; description: string; colorClass: string; bgClass: string }) => {
  return (
    <ScrollAnimation>
      <div className="relative rounded-xl border border-border/60 bg-card p-5 md:p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 ease-out text-center group cursor-default overflow-hidden">
        <div className={`w-11 h-11 rounded-xl ${bgClass} flex items-center justify-center mx-auto mb-3.5 ${colorClass} group-hover:scale-110 transition-transform duration-300`}>
          {icon}
        </div>
        <h4 className="text-sm font-semibold mb-1.5 tracking-tight text-foreground">{title}</h4>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </ScrollAnimation>
  );
};

interface StatCardProps {
  number: string;
  label: string;
  subtext: string;
  icon: React.ReactNode;
}

const StatCard = ({ number, label, subtext, icon }: StatCardProps) => {
  return (
    <div className="text-center group">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3 text-primary group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <div className="text-xl md:text-2xl font-bold text-foreground mb-1 tracking-tight">
        {number}
      </div>
      <div className="text-sm font-medium text-foreground/80 mb-0.5">{label}</div>
      <div className="text-xs text-muted-foreground">{subtext}</div>
    </div>
  );
};

export default FeatureSection;
